import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Home, Clock, UtensilsCrossed } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';

const OrderConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const orderId = searchParams.get('order');
  const tableNumber = searchParams.get('table');

  const texts = {
    tr: {
      title: 'Siparişiniz Bize Ulaştı!',
      subtitle: 'Teşekkür ederiz, siparişiniz başarıyla alındı.',
      orderNumber: 'Sipariş Numarası',
      tableInfo: 'Masa',
      message: 'Siparişiniz hazırlanıyor. En kısa sürede size ulaşacak.',
      tableMessage: 'Siparişiniz hazırlanıyor. Garsonumuz en kısa sürede masanıza getirecek.',
      estimatedTime: 'Tahmini Hazırlık',
      minutes: 'dakika',
      newOrder: 'Yeni Sipariş Ver',
      goHome: 'Ana Sayfaya Dön',
      trackOrder: 'Siparişlerim'
    },
    en: {
      title: 'Your Order Has Been Received!',
      subtitle: 'Thank you, your order has been successfully placed.',
      orderNumber: 'Order Number',
      tableInfo: 'Table',
      message: 'Your order is being prepared. It will reach you shortly.',
      tableMessage: 'Your order is being prepared. Our waiter will bring it to your table shortly.',
      estimatedTime: 'Estimated Preparation',
      minutes: 'minutes',
      newOrder: 'New Order',
      goHome: 'Go to Home',
      trackOrder: 'My Orders'
    },
    me: {
      title: 'Vaša Narudžba je Primljena!',
      subtitle: 'Hvala vam, vaša narudžba je uspješno kreirana.',
      orderNumber: 'Broj Narudžbe',
      tableInfo: 'Sto',
      message: 'Vaša narudžba se priprema. Uskoro će stići do vas.',
      tableMessage: 'Vaša narudžba se priprema. Naš konobar će je uskoro donijeti na vaš sto.',
      estimatedTime: 'Procijenjeno Vrijeme',
      minutes: 'minuta',
      newOrder: 'Nova Narudžba',
      goHome: 'Idi na Početnu',
      trackOrder: 'Moje Narudžbe'
    }
  };

  const txt = texts[language] || texts.en;

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl p-8 md:p-12 shadow-lg max-w-md w-full text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="w-20 h-20 bg-basil rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <CheckCircle className="w-10 h-10 text-white" />
        </motion.div>

        <h1 className="font-playfair text-3xl font-bold text-charcoal mb-2" data-testid="confirmation-title">
          {txt.title}
        </h1>
        <p className="text-charcoal/60 mb-6">{txt.subtitle}</p>

        <div className="bg-sand rounded-2xl p-6 mb-4">
          {tableNumber && (
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-10 h-10 bg-terracotta rounded-full flex items-center justify-center">
                <span className="font-bold text-white">{tableNumber}</span>
              </div>
              <span className="text-charcoal/70">{txt.tableInfo}</span>
            </div>
          )}
          <p className="text-sm text-charcoal/60 mb-1">{txt.orderNumber}</p>
          <p className="font-mono text-xl font-bold text-charcoal" data-testid="order-number">
            #{orderId?.slice(-8).toUpperCase()}
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 bg-basil/10 rounded-xl p-3 mb-4">
          <Clock className="w-5 h-5 text-basil" />
          <span className="text-basil font-medium">{txt.estimatedTime}: 15-25 {txt.minutes}</span>
        </div>

        <p className="text-charcoal/70 mb-6">{tableNumber ? txt.tableMessage : txt.message}</p>

        <div className="space-y-3">
          {tableNumber ? (
            <Button
              onClick={() => navigate(`/menu?table=${tableNumber}`)}
              className="w-full bg-terracotta text-white hover:bg-terracotta/90 rounded-full py-6"
              data-testid="new-order-btn"
            >
              <UtensilsCrossed className="w-5 h-5 mr-2" />
              {txt.newOrder}
            </Button>
          ) : (
            <>
              <Button
                onClick={() => navigate('/orders')}
                className="w-full bg-terracotta text-white hover:bg-terracotta/90 rounded-full py-6"
                data-testid="track-order-btn"
              >
                {txt.trackOrder}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full rounded-full py-6"
                data-testid="go-home-btn"
              >
                <Home className="w-5 h-5 mr-2" />
                {txt.goHome}
              </Button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default OrderConfirmation;
